import { useMemo } from 'react'

import { useWindow } from '@krp/client-runtime'
import {
  FEEDBACK_SCOPES,
  ResolveScreen,
  ScannerScreen,
  aqsiSaleOperation,
  createResolveActions,
  createScannerActions,
  makeSelectResolveVm,
  mineSeatOf,
  resolveDraftOf,
  ridKey,
} from '@krp/resource-aqsi-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { useMySession } from './useMySession'

/** Сканер окна продажи (весь экран): итог кода — из итога команд места продажи. */
export function AqsiSaleScannerBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const { session } = useMySession(resourceId)
  const registerKey = mineSeatOf(session)?.registerKey ?? null
  const feedback = useAppSelector((state: RootState) =>
    registerKey === null ? undefined : state.aqsi.feedback.byKey[ridKey(resourceId, FEEDBACK_SCOPES.sale(registerKey))],
  )
  const message =
    registerKey === null
      ? 'Вы не работаете за кассой этого кабинета — начните работу в РМК, затем сканируйте'
      : feedback?.command === 'sale.scan'
        ? feedback.note
        : null
  const actions = useMemo(() => createScannerActions(resourceId, dispatch, window.close), [dispatch, resourceId, window])
  return <ScannerScreen message={message} actions={actions} />
}

/** Ручная сверка операции: модальное окно поверх продажи (rid и операция — в адресе окна). */
export function AqsiSaleResolveBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const operationId = window.params.operationId ?? ''
  const args = useMemo(() => ({ resourceId, operationId }), [operationId, resourceId])
  aqsiSaleOperation.useQuerySubscription(args, { skip: resourceId === '' || operationId === '' })
  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectResolveVm()
    const selectOperation = aqsiSaleOperation.select(args)
    const feedbackKey = ridKey(resourceId, FEEDBACK_SCOPES.resolve(operationId))
    return (state: RootState) =>
      selectPackageVm({
        resourceId,
        operationId,
        operation: selectOperation(state),
        draft: resolveDraftOf(state.aqsi.sale, resourceId, operationId),
        feedback: state.aqsi.feedback.byKey[feedbackKey],
      })
  }, [args, operationId, resourceId])
  const vm = useAppSelector(selectVm)
  const version = useAppSelector((state: RootState) => aqsiSaleOperation.select(args)(state).data?.version ?? null)
  const actions = useMemo(
    () => createResolveActions(vm, version, dispatch, window.close),
    [dispatch, version, vm, window],
  )
  return <ResolveScreen vm={vm} actions={actions} />
}
